<template>
  <div class="operation-report-list-table h-full">
    <div class="filter-type-timeline-download w-full flex items-center">
      <div class="filter-type-timeline-col flex items-center">
        <div class="filter-type aisea-text-18-600-dark-gray py-2">
          {{ selectedServiceName }}
        </div>
        <timeline-select
          @apply="onApply"
          :initialSelectedTimeline="selectedTimeline"
        ></timeline-select>
      </div>
      <div class="download-col ml-auto">
        <download-button
          v-if="isDownloadable"
          @click="showDownloadDialog"
        ></download-button>
      </div>
    </div>

    <div id="filter-select-area" class="flex flex-wrap py-2">
      <treeselect
        v-model="selectedFilterDepartmentNames"
        :options="filterDepartmentOptions"
        :multiple="true"
        placeholder="すべての部署"
        :limit="1"
        :limitText="count => `+${count}`"
        noOptionsText="なし"
        class="mr-4 my-1"
        @input="onFilterChanged"
        noResultsText="見つかりませんでした"
      ></treeselect>
      <treeselect
        v-model="selectedFilterAccountIds"
        :options="filterAccountOptions"
        :multiple="true"
        placeholder="すべての担当者"
        :limit="1"
        :limitText="count => `+${count}`"
        noOptionsText="なし"
        class="mr-4 my-1"
        @input="onFilterChanged"
        noResultsText="見つかりませんでした"
      ></treeselect>
      <treeselect
        v-model="selectedFilterVesselIds"
        :options="filterVesselOptions"
        :multiple="true"
        placeholder="すべての船舶"
        :limit="1"
        :limitText="count => `+${count}`"
        noOptionsText="なし"
        class="mr-4 my-1"
        @input="onFilterChanged"
        noResultsText="見つかりませんでした"
      ></treeselect>
      <treeselect
        v-model="selectedFilterSituationIds"
        :options="filterSituationOptions"
        :multiple="true"
        placeholder="すべての対応状況"
        :limit="1"
        :limitText="count => `+${count}`"
        noOptionsText="なし"
        class="mr-4 my-1"
        @input="onFilterChanged"
        noResultsText="見つかりませんでした"
      ></treeselect>
    </div>

    <vs-divider :color="AISEA_COLOR.LIGHT_GRAY" class="my-0" />

    <!-- AgGrid Table -->
    <!-- TODO: 並び替えに関数メモ
       :grid-ready="gridOptions.api.refreshCells"
       @columnMoved="onColumnMoved"
       :suppressMovableColumns="false"
     -->
    <ag-grid-vue
      :components="components"
      :gridOptions="gridOptions"
      @sortChanged="onSort"
      @rowClicked="onRowClicked"
      class="ag-theme-material w-full ag-grid-table"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :rowData="tableData"
      domLayout="autoHeight"
      rowSelection="multiple"
      colResizeDefault="shift"
      :animateRows="true"
      :floatingFilter="false"
      :suppressMovableColumns="true"
      :suppressPaginationPanel="true"
      :suppressDragLeaveHidesColumns="true"
      :suppressCellFocus="true"
      :suppressRowClickSelection="true"
      :suppressNoRowsOverlay="false"
    />

    <!-- ページネーション領域 -->
    <operation-report-list-pagination
      :currentPage="currentPage"
      :pageSize="pageSize"
      :totalElement="totalElements"
      @onChangePageSize="onChangePageSize"
      @onChangePage="onChangePage"
    ></operation-report-list-pagination>

    <!-- ダウンロードダイアログ -->
    <report-csv-download-dialog
      :active="isDownloadDialogActive"
      @accept="onDownloadAccept"
      @cancel="onDownloadCancel"
    />
  </div>
</template>

<script>
import operationDepartmentService from "@/services/operations/operationDepartmentService";
import operationService from "@/services/operations/operationService";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import operationReportService from "@/services/operations/operationReportService";
import DownloadButton from "./DownloadButton";

import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import CellRendererBadge, {
  cssVariableNames,
} from "./cell-renderer/CellRendererBadge";
import TimelineSelect from "./TimelineSelect";

import { DeviceInfoMixin, ColorMixin } from "@/mixins";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import OperationReportListPagination from "../../components/OperationReportListPagination.vue";
import ReportCsvDownloadDialog from "@/views/enterprise/addon/operation/components/dialogs/ReportCsvDownloadDialog.vue";
import moment from "moment";
import { OPERATIONS } from "@/config/const.js";
import { notifySystemError, applyUnit, applyFormat } from "@/utils/operations";
import { createNamespacedHelpers } from "vuex";
import isObject from "lodash/isObject";
import isDate from "lodash/isDate";
import isNumber from "lodash/isNumber";
import isString from "lodash/isString";
const { mapActions, mapGetters } = createNamespacedHelpers("operationReports");
const { mapGetters: mapGettersOperations } = createNamespacedHelpers(
  "operations"
);
const { mapGetters: mapGettersOCs } = createNamespacedHelpers(
  "operationColumns"
);
const { mapGetters: mapGettersOperationAccounts } = createNamespacedHelpers(
  "operationAccounts"
);
const { mapGetters: mapGettersOperationWorkflows } = createNamespacedHelpers(
  "operationWorkflows"
);
const { mapGetters: mapGettersOVs } = createNamespacedHelpers(
  "operationVessels"
);
const { mapGetters: mapGettersEWs } = createNamespacedHelpers(
  "enterpriseWorkflows"
);
const { mapGetters: mapGettersEFs } = createNamespacedHelpers(
  "enterpriseForms"
);

const NO_DATA_MESSAGE = "表示するレポートがありません";

export default {
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererBadge,
    Treeselect,
    OperationReportListPagination,
    TimelineSelect,
    DownloadButton,
    ReportCsvDownloadDialog,
  },
  mixins: [DeviceInfoMixin, ColorMixin],
  props: {
    aoEnterpriseId: {
      type: String,
      default: "ALL", // 全て
    },
    initialSearchConditions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      gridApi: null,
      gridOptions: {
        localeText: {
          noRowsToShow: `<div id="no-rows-manning-vessel-list" class="aisea-text-16-400-black">${NO_DATA_MESSAGE}</div>`,
        },
        context: {
          componentParent: this,
        },
      },
      domLayout: "autoHeight",

      // 画面に表示するページの情報
      currentPage: 1, // api の page + 1
      pageSize: 25, // 25 | 50 | 75 | 100

      sortType: null, // "ASC" | "DESC"
      sortColumnKey: null,
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true,
        icons: {
          sortAscending:
            '<span class="material-icons">keyboard_arrow_up</span>',
          sortDescending:
            '<span class="material-icons">keyboard_arrow_down</span>',
          sortUnSort: '<span class="material-icons">unfold_more</span>',
        },
        unSortIcon: true,
        comparator: () => {
          return;
        },
      },
      components: {
        CellRendererBadge,
      },

      // 検索条件
      selectedFilterDepartmentNames: [], // string[]
      selectedFilterAccountIds: [], // string[]
      selectedFilterVesselIds: [], // string[]
      selectedFilterSituationIds: [], // string[]
      selectedTimeline: {
        startDate: null, // Date | null
        endDate: null, // Date | null
      },

      // 検索のオプション作成に使用
      departmentNames: [], // string[]

      isCreated: false, // createdの処理が完了したかどうか

      isDownloadDialogActive: false, // ダウンロードダイアログ表示フラグ
    };
  },
  watch: {
    searchConditions: {
      handler() {
        if (!this.isCreated) return;
        this.fetchTableData(this.searchConditions);
      },
      deep: true,
    },
  },
  computed: {
    // 全てのレポートで使用するカラム
    fixedCommonColumns() {
      return [
        {
          headerName: "船舶名",
          field: "vessel_name",
          minWidth: 170,
          flex: 1,
        },
        {
          headerName: "レポート名",
          field: "service_name",
          minWidth: 180,
          flex: 1,
        },
        {
          headerName: "日付",
          field: "updated_at",
          minWidth: 150,
          flex: 1,
        },
        {
          headerName: "部署名",
          field: "department_name",
          minWidth: 180,
          flex: 1,
        },
        {
          headerName: "担当者名",
          field: "account_name",
          minWidth: 120,
          flex: 1,
        },
        {
          headerName: "次のアクション",
          field: "next_action",
          minWidth: 210,
          flex: 1,
          sortable: false,
        },
        {
          headerName: "対応状況",
          field: "situation",
          minWidth: 130,
          width: 120,
          cellRenderer: "CellRendererBadge",
          cellRendererParams: params => {
            const situation = params.value;
            const text = situation
              ? this.$t(`Views.Operation.Situation.${situation}`)
              : "";
            const budgeStyle = OPERATIONS.SITUATION_BADGE_STYLES[situation];
            return {
              text,
              cssVariables: {
                [cssVariableNames.badgeBgColor]: budgeStyle.backgroundColor,
                [cssVariableNames.badgeColor]: budgeStyle.color,
              },
            };
          },
        },
      ];
    },

    columnDefs() {
      // 全てのレポートの場合は固定の共通カラム定義を使用
      if (!this.hasSelectedAoEnterpriseIds) return this.fixedCommonColumns;
      // 個別のレポートの場合は動的カラム定義を使用
      return this.dynamicColumns.map(column => {
        const fieldSuffix = column.formVersionItemIdList[0]
          ? column.formVersionItemIdList[0]
          : column.commonInfo;
        const isCommonColumn = column.commonInfo != null;
        if (isCommonColumn) {
          // 共通カラムの場合は固定の共通カラム定義を使用し、APIから送られるカラム情報で上書き
          const targetFixedCommonColumn = this.fixedCommonColumns.find(
            fixedColumn => fixedColumn.field.toUpperCase() === column.commonInfo
          );
          if (targetFixedCommonColumn) {
            return {
              ...targetFixedCommonColumn,
              minWidth: column.minWidth,
              sortOrder: column.sortOrder,
              headerName: column.headerName,
            };
          } else {
            throw new Error(`common column ${column.commonInfo} not found`);
          }
        } else {
          return {
            headerName: column.headerName,
            field: `${column.id}-${fieldSuffix}`,
            minWidth: column.minWidth || 150,
            flex: 1,
            sortable: false,
            valueGetter: value => {
              return this.valueGetterCreator(
                column.formVersionItemIdList,
                value.data.report
              );
            },
          };
        }
      });
    },

    // 個別レポートで使用する動的カラム
    dynamicColumns() {
      return this.selectedFilterOperationColumns.map(oc => {
        const formVersionItemIdList = oc.ao_enterprise_column_form_version_items.map(
          fvi => fvi.form_version_item_id
        );

        return {
          id: oc.id,
          headerName: oc.name,
          formVersionItemIdList,
          minWidth: oc.min_width,
          commonInfo: oc.common_info,
          flex: 1,
          sortOrder: oc.sort_order, // columnDef作成時に、この順番で追加 / ColumnDefには入れない
        };
      });
    },
    selectedFilterOperationColumns() {
      if (!isObject(this.ocGroupByAoEnterpriseId)) return [];
      return this.ocGroupByAoEnterpriseId[this.aoEnterpriseId] || [];
    },
    tableData() {
      if (!Array.isArray(this.validEnterpriseVesselReports)) return [];

      // ここでは行のデータを取得するだけにして、表示方法はformatterやcellRendererでできるだけ行う

      return this.validEnterpriseVesselReports.map(report => {
        const ow =
          this.operationWorkflowMapByAoEnterpriseWorkflowVesselId[
            report.ao_enterprise_workflow_vessel_id
          ] || {};
        const operation = this.operationMap[ow.ao_enterprise_id] || {};
        // 部署
        const wv =
          this.operationWorkflowVesselMap[
            report.ao_enterprise_workflow_vessel_id
          ] || {}; // WorkflowVessel
        const ws = this.workflowStatusMap[report.workflow_status_id] || {}; // WorkflowStatus
        return {
          id: report.id,
          operation,
          vessel_name: (() => {
            const ov = this.ovMapByEnterpriseVesselId[wv.enterprise_vessel_id];
            if (!isObject(ov)) return "";
            if (!isObject(ov.vessel)) return "";
            const { vessel: ovV } = ov;
            return ovV.name_ja || ovV.name;
          })(),

          service_name: operation.name || "",
          updated_at: moment(report.updated_at).format("YYYY/MM/DD"), // 日付
          department_name: (Array.isArray(
            wv.ao_enterprise_workflow_vessel_departments
          )
            ? wv.ao_enterprise_workflow_vessel_departments
            : []
          )
            .map(d => d.name)
            .join(", "),
          // 担当者名
          account_name: (Array.isArray(
            wv.ao_enterprise_workflow_vessel_accounts
          )
            ? wv.ao_enterprise_workflow_vessel_accounts
            : []
          )
            .filter(
              aoEnterpriseWVA =>
                aoEnterpriseWVA && aoEnterpriseWVA.is_responsible
            )
            .filter(
              aoEnterpriseWVA =>
                this.reportSearchableOEAMapByAccountId[
                  aoEnterpriseWVA.account_id
                ]
            )
            .map(
              aoEnterpriseWVA =>
                this.reportSearchableOEAMapByAccountId[
                  aoEnterpriseWVA.account_id
                ].profile
            )
            .map(profile => profile.name)
            .join(", "),
          next_action: ws.next_action_label || "",
          situation: this.calcSituation(ws),
          report,
        };
      });
    },
    ...mapGetters({
      page: "page",
      totalElements: "totalElements",
      validEnterpriseVesselReports: "validEnterpriseVesselReports",
      fviMapOfReportIdToFormVersionItemId:
        "formVersionItemMapOfReportIdToFormVersionItemId",
    }),
    ...mapGettersOperations(["operationMap"]),
    ...mapGettersOperationAccounts({
      reportSearchableOEAs: "reportSearchableOperationEnterpriseAccounts",
      reportSearchableOEAMapByAccountId:
        "reportSearchableOperationEnterpriseAccountMapByAccountId",
    }),
    ...mapGettersOperationWorkflows([
      "operationWorkflowMapByAoEnterpriseWorkflowVesselId",
      "operationWorkflowVesselMap",
    ]),
    ...mapGettersOVs({
      validOperationVessels: "validOperationVessels",
      ovMapByEnterpriseVesselId: "operationVesselMapByEnterpriseVesselId",
    }),
    ...mapGettersEWs({
      workflowStatusMap: "workflowStatusMap",
    }),
    ...mapGettersEFs({
      enterpriseFormVersionItemMap: "enterpriseFormVersionItemMap",
    }),
    ...mapGettersOCs({
      ocGroupByAoEnterpriseId: "operationColumnGroupByAoEnterpriseId",
    }),
    filterDepartmentOptions() {
      if (!Array.isArray(this.departmentNames)) return [];
      return this.departmentNames
        .filter(dn => operationDepartmentService.isOperationDepartmentName(dn))
        .map(dn => ({
          id: dn,
          label: dn,
        })); // { id: string, label: string }[]
    },
    filterAccountOptions() {
      const oEAs = this.reportSearchableOEAs;
      if (!Array.isArray(oEAs)) return [];
      return oEAs.map(oea => {
        return {
          id: oea.account.id,
          label: oea.profile.name,
        };
      }); // { id: string, label: string }[]
    },
    filterVesselOptions() {
      if (!Array.isArray(this.validOperationVessels)) return [];
      return this.validOperationVessels.map(ov => {
        const v = ov.vessel;
        return {
          id: ov.id,
          label: v.name_ja || v.name || "",
        };
      }); // { id: string, label: string }[]
    },
    filterSituationOptions() {
      const { SITUATIONS: s } = OPERATIONS;
      return [s.IN_PROGRESS, s.WAITING, s.COMPLETE].map(situation => {
        return {
          id: situation,
          label: this.$t(`Views.Operation.Situation.${situation}`),
        };
      }); // { id: string, label: string }[]
    },

    isDownloadable() {
      if (!isObject(this.selectedOperation)) return false;
      return this.selectedOperation.download_available;
    },
    // : Operation | undefined
    selectedOperation() {
      return this.operationMap[this.aoEnterpriseId];
    },
    // : string
    selectedServiceName() {
      if (!isObject(this.selectedOperation)) {
        return this.$t(`Views.Operation.Report.All`);
      }

      return this.selectedOperation.name;
    },
    selectedAoEnterpriseIds() {
      if (isNumber(this.aoEnterpriseId)) return [];
      if (this.aoEnterpriseId === "ALL") return []; // ALL は すべて として扱う
      return [this.aoEnterpriseId];
    },
    hasSelectedAoEnterpriseIds() {
      return this.selectedAoEnterpriseIds.length > 0;
    },
    selectedDateFrom() {
      if (!isObject(this.selectedTimeline)) return null;
      if (!isDate(this.selectedTimeline.startDate)) return null;
      return moment(this.selectedTimeline.startDate).format("YYYY-MM-DD");
    },
    selectedDateTo() {
      if (!isObject(this.selectedTimeline)) return null;
      if (!isDate(this.selectedTimeline.endDate)) return null;
      return moment(this.selectedTimeline.endDate).format("YYYY-MM-DD");
    },
    searchConditions() {
      return {
        accountIds: this.selectedFilterAccountIds,
        dateFrom: this.selectedDateFrom,
        dateTo: this.selectedDateTo,
        departmentNames: this.selectedFilterDepartmentNames,
        enterpriseVesselIds: this.selectedFilterVesselIds,
        aoEnterpriseIds: this.selectedAoEnterpriseIds,
        page: this.getCurrentPage(), // this.currentPage - 1
        situations: this.selectedFilterSituationIds,
        size: this.pageSize,
        sortColumn: this.sortColumnKey,
        sortOrder: this.sortType,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    },
  },
  methods: {
    // TODO: 他のところでも使えるなら共通化してもいいかも
    isEmptyReportFormVersionItem(reportFormVersionItem) {
      if (!isObject(reportFormVersionItem)) return true;
      if (
        reportFormVersionItem.value_date == null &&
        reportFormVersionItem.value_date_time == null &&
        reportFormVersionItem.value_number == null &&
        reportFormVersionItem.value_string == null &&
        reportFormVersionItem.value_time == null &&
        reportFormVersionItem.value_files.length == 0
      ) {
        return true;
      } else {
        return false;
      }
    },
    valueGetterCreator(formVersionItemIdList, report) {
      // formVersionItemIdListを表示優先順位高い順に受け取り、それぞれに対応するvalueを格納する
      const valueList = formVersionItemIdList.map(formVersionItemId => {
        const enterpriseForm = this.enterpriseFormVersionItemMap[
          formVersionItemId
        ]; // これはcolumnを使用するので、データ作成には移動できない

        // フォームのvalueが空かどうかを判定し、空の場合はnullかnullableLabelを返す
        const item = this.getFormVersionItemFromReport(
          report,
          formVersionItemId
        );
        if (this.isEmptyReportFormVersionItem(item)) {
          if (item && operationReportService.isNullableChecked(item)) {
            return enterpriseFormService.getNullableLabelFromEnterpriseFormVersionItem(
              enterpriseForm
            );
          }
          return null;
        }

        // 画像ファイルのフォーマッター
        const isSingleImageFile = enterpriseFormService.isSingleImageFile(
          enterpriseForm
        );
        if (isSingleImageFile) {
          return this.applyFormatOfSingleImage(
            enterpriseForm,
            this.getFormVersionItemFromReport(report, formVersionItemId)
          );
        }

        // 通常のフォーマッター
        const formVersionItem = this.getFormVersionItemFromReport(
          report,
          formVersionItemId
        );

        const reportValue = operationReportService.getValueFromReportFormVersionItem(
          formVersionItem
        );
        // 行全体に関わるデータはできるだけデータ作成処理の方に移動する
        const formattedReportValue = applyFormat(reportValue, enterpriseForm);

        return applyUnit(formattedReportValue, enterpriseForm.unit);
      });

      // value表示の優先順位の高い順から見ていき、一番最初に値がある要素を返す
      for (let index = 0; index < valueList.length; index++) {
        const element = valueList[index];
        if (element) {
          return element;
        }
      }
      return null;
    },

    applyFormatOfSingleImage(enterpriseForm, reportFormVersionItem) {
      if (!enterpriseFormService.isSingleImageFile(enterpriseForm)) {
        return this.$t("Views.Operation.Report.withoutImage");
      }
      if (
        !operationReportService.hasReportFormVersionItemFiles(
          reportFormVersionItem
        )
      ) {
        return this.$t("Views.Operation.Report.withoutImage");
      }
      return this.$t("Views.Operation.Report.withImage");
    },
    getFormVersionItemFromReport(
      report, // ReportFormVersionItem
      formVersionItemId // OperationColumnItem
    ) {
      const r = report;
      const formVersionItemMap =
        this.fviMapOfReportIdToFormVersionItemId[r.id] || {};
      return formVersionItemMap[formVersionItemId];
    },
    // :string
    calcSituation(
      ws // : WorkflowStatus
    ) {
      if (!isObject(ws)) return "";

      const { SITUATIONS: s } = OPERATIONS;
      const sl = ws.supplement_label;

      if (sl === "陸上管理者") return s.IN_PROGRESS;
      if (sl === "船員") return s.WAITING;
      if (sl === "") return s.COMPLETE;
      return ""; // fallback
    },
    async setFilterOptionsData() {
      Promise.all([operationDepartmentService.getOperationDepartmentNames()])
        .then(([departments, _]) => {
          this.departmentNames = departments.data.department_names || [];
        })
        .catch(_e => {
          notifySystemError(this);
        });
    },
    ...mapActions(["setOperationReports"]),
    onApply(
      selectedTimeline // : {startDate: Date | null, endDate: Date | null}
    ) {
      this.selectedTimeline = selectedTimeline;
    },
    onFilterChanged() {
      this.currentPage = 1;
    },

    getCurrentPage() {
      return this.currentPage - 1;
    },

    onSort(object) {
      const { SORT_COLUMNS: sc } = OPERATIONS;
      let sortColumn = object.columnApi.columnModel.viewportColumns.find(
        element => {
          return element.sort != null;
        }
      );
      this.sortType = sortColumn ? sortColumn.sort.toUpperCase() : null;
      this.sortColumnKey = sortColumn
        ? sc[sortColumn.colId.toUpperCase()]
        : null;
      this.currentPage = 1;
    },

    onRowClicked(row) {
      if (!isObject(row)) return;
      if (!isObject(row.data)) return;
      if (!isString(row.data.id)) return;

      if (!operationService.isValidOperation(row.data.operation)) return;
      const nextRoute = this.$router.resolve({
        name: "addon-operation-reports-show",
        params: {
          aoEnterpriseId: row.data.operation.id,
          reportId: row.data.id,
        },
      });
      window.open(nextRoute.href, "_blank", "noreferrer", "noopener"); // 別タブで開く
    },

    onChangePage(value) {
      this.currentPage = value;
    },

    onChangePageSize(size) {
      this.pageSize = size;
      this.currentPage = 1;
    },

    clearSearchQueryString() {
      this.searchQueryString = "";
    },

    async fetchTableData() {
      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        await this.setOperationReports(this.searchConditions);
      } catch (e) {
        notifySystemError(this);
      } finally {
        this.$nextTick(() => {
          this.$vs.loading.close();
        });
      }
    },
    setInitialSearchConditionsToData() {
      const { initialSearchConditions: isc } = this;

      if (isObject(isc)) {
        this.selectedFilterAccountIds = isc.accountIds || [];
        // レポートは初期化時は全てを表示
        this.setInitialSelectedTimelineToData(); // 開始日時、終了日時
        this.selectedFilterDepartmentNames = isc.departmentNames || [];
        this.selectedFilterVesselIds = isc.enterpriseVesselIds || [];
        this.currentPage = 1; // 初期化時は最初のページを表示
        this.selectedFilterSituationIds = isc.situations || [];
        this.pageSize = isc.size || 25;
        this.sortColumnKey = isc.sortColumn || null;
        this.sortType = isc.sortOrder || null;
        // tz はブラウザのタイムゾーンを使用
      }
    },

    setInitialSelectedTimelineToData() {
      const { initialSearchConditions: isc } = this;

      if (!isObject(isc)) return;
      if (!isString(isc.tz)) return;

      const dateFromMoment = moment.tz(
        isc.dateFrom,
        moment.ISO_8601,
        true,
        isc.tz
      );
      const dateToMoment = moment.tz(isc.dateTo, moment.ISO_8601, true, isc.tz);
      this.selectedTimeline = {
        startDate: dateFromMoment.isValid() ? dateFromMoment.toDate() : null,
        endDate: dateToMoment.isValid() ? dateToMoment.toDate() : null,
      };
    },

    // ダウンロードダイアログ関係の処理
    async onDownloadAccept() {
      try {
        setTimeout(() => {
          this.isDownloadDialogActive = false;
        }, 0);
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        const res = await operationReportService.downloadOperationReportCsv({
          aoEnterpriseId: this.aoEnterpriseId,
        });
        if (!res || !res.status) {
          throw Error("Failed to download csv file");
        }
        const dateTimeString = moment().format("YYYYMMDDHHmmss");
        this.saveFile(
          res.data,
          this.selectedServiceName + "_" + dateTimeString + ".csv"
        );
      } catch (e) {
        notifySystemError(this);
      } finally {
        this.$vs.loading.close();
      }
    },
    saveFile(csv, fileName) {
      const blob = new Blob([csv], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
    },
    onDownloadCancel() {
      setTimeout(() => {
        this.isDownloadDialogActive = false;
      }, 0);
    },
    showDownloadDialog() {
      this.isDownloadDialogActive = true;
    },
    closeDownloadDialog() {
      this.isDonwloadDialogActive = false;
    },
  },

  async created() {
    this.setInitialSearchConditionsToData();
    await this.setFilterOptionsData();
    await this.fetchTableData();
    this.isCreated = true;
  },

  mounted() {
    let routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.operation-report-list-table {
  .ag-grid-table {
    height: 100%;

    font-family: $aisea-font-family;
    font-size: 16px;
    font-weight: 400;

    .ag-header-cell-text {
      font-family: $aisea-font-family;
      font-size: 12px;
      font-weight: 600;
    }

    .ag-cell {
      padding-left: 10px;
      padding-right: 10px;
    }
    .ag-header-cell {
      padding-left: 10px;
      padding-right: 10px;
    }
  }
  .ag-header-cell {
    padding-right: 0;
  }
  .vs-tabs--content {
    padding: 4px 0 0 0;
  }

  ::v-deep .vs-pagination--ul {
    span {
      font-size: 12px;
      font-family: $aisea-font-family;
      font-weight: 400;
    }
  }
  ::v-deep .vue-treeselect__limit-tip-text {
    color: $aisea-blue !important;
  }

  ::v-deep .vue-treeselect--open .vue-treeselect__control {
    border-top-color: $aisea-blue !important;
    border-left-color: $aisea-blue !important;
    border-right-color: $aisea-blue !important;
  }

  ::v-deep .vue-treeselect__menu {
    box-shadow: 2px 5px 10px 0.5px #00000026;
    border-bottom-color: $aisea-blue !important;
    border-left-color: $aisea-blue !important;
    border-right-color: $aisea-blue !important;
  }

  ::v-deep .vue-treeselect__multi-value-item {
    color: $aisea-blue !important;
  }

  ::v-deep .vue-treeselect__control {
    width: 190px;
    height: 36px;
  }

  .filter-type-timeline-col {
    gap: 30px;
  }
}
</style>
